import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { designSystem } from '@yola/ws-ui';
import { constants } from '@yola/subscription-manager-js';
import getStepLineItems from './helpers/get-step-line-items';
import getPlanItems from './helpers/get-plan-items';
import actionIds from './constants/action-ids';
import { t } from '../localization';
import yoproductsServiceClient from '../common/helpers/yoproducts-service-client';
import generatePaymentUrl from '../utils/generate-payment-url';
import useMatchBreakpoint from '../common/hooks/use-match-breakpoint';
import breakpoints from '../common/constants/breakpoints';

const {
  SubscriptionType: { DOMAIN_SUBSCRIPTION },
  SubscriptionTerm: { ANNUAL, MONTHLY },
} = constants;
const { Paragraph, Heading, Container, Row, Col, Box, RadioSelectCard, Stack, ActionButton } =
  designSystem;

const AutomaticPlanSelector = ({
  actionId,
  plan,
  isB2C,
  partnerId,
  siteId,
  discountType = null,
  domainName = '',
  currency = 'USD',
  successUrl = '',
  onSelect = Function.prototype,
  onSubmit,
  onCancel,
}) => {
  const [prices, setPrices] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTerm, setTerm] = useState(
    actionId === actionIds.GET_FREE_DOMAIN ? MONTHLY : ANNUAL
  );

  const isXsBreakpoint = useMatchBreakpoint(breakpoints.XS);
  const isSmBreakpoint = useMatchBreakpoint(breakpoints.SM);
  const isMdBreakpoint = useMatchBreakpoint(breakpoints.MD);

  const stepLineItems = useMemo(
    () => getStepLineItems({ domainName, actionId, currency }),
    [domainName, actionId, currency]
  );
  const planItems = getPlanItems({
    plan,
    prices,
    currency,
    domainName,
    isLoading,
    selectedTerm,
    actionId,
  });

  const handleClick = () => {
    const paymentParams = {
      products: [plan.type, !!domainName && DOMAIN_SUBSCRIPTION].filter(Boolean),

      term: selectedTerm,
      success_url: successUrl,
      latitude_site_id: siteId,
      trial_package: '1',
      ...(domainName && { domain_name: domainName }),
    };

    const paymentUrl = generatePaymentUrl(paymentParams, isB2C);

    onSubmit(paymentUrl);
  };

  const handleSelect = (term) => {
    onSelect(term);
    setTerm(term);
  };

  const handleCancel = () => {
    onCancel();
  };

  useEffect(() => {
    const yoproducts = yoproductsServiceClient.get(isB2C);
    const queryParams = {
      ...(discountType && { discount: discountType }),
    };

    yoproducts.requests.partnerProductsTree({ partnerId }, queryParams).then((data) => {
      setPrices(data);
      setIsLoading(false);
    });
  }, [isB2C, partnerId, discountType]);

  return (
    <Container fluid="md">
      <Row className="justify-content-center">
        <Col xxl={6} xl={6} lg={8} md={10} sm={10} className="g-0">
          <Heading align="center" type="heading-1">
            <span
              // eslint-disable-next-line yola/react/no-danger
              dangerouslySetInnerHTML={{
                __html: t('Start Your Free{space}Website Trial', {
                  space: isSmBreakpoint ? ' ' : '<br />',
                }),
              }}
            />
          </Heading>
          <Box
            paddingLeft="spacing-xs"
            paddingRight="spacing-xs"
            paddingTop="spacing-xl"
            paddingBottom="spacing-xl"
            breakpoints={{
              md: {
                paddingTop: 'spacing-2-xl',
                paddingLeft: 'spacing-0',
                paddingRight: 'spacing-0',
              },
            }}
          >
            <Stack gap="spacing-xl">
              <div className="ws-automatic-plan-selector-step-line">
                {stepLineItems.map((item) => (
                  <div
                    key={item.id}
                    className={classNames('ws-automatic-plan-selector-step-line__item', {
                      'ws-automatic-plan-selector-step-line__item--active': item.isActive,
                    })}
                  >
                    <div className="ws-automatic-plan-selector-step-line__item-marker" />
                    <div className="ws-automatic-plan-selector-step-line__item-content">
                      <Stack>
                        <Heading type="heading-4">{item.title}</Heading>
                        <Paragraph>
                          {/* eslint-disable-next-line yola/react/no-danger */}
                          <span dangerouslySetInnerHTML={{ __html: item.description }} />
                        </Paragraph>
                      </Stack>
                    </div>
                  </div>
                ))}
              </div>
              <Stack gap="spacing-xs">
                {planItems.map((item) => (
                  <RadioSelectCard
                    key={item.id}
                    withDecoration={item.withDecoration}
                    selected={selectedTerm === item.id}
                    title={item.title}
                    label={item.label}
                    description={item.description}
                    summaryLabel={item.summaryLabel}
                    summaryNote={item.summaryNote}
                    onSelect={() => handleSelect(item.id)}
                  />
                ))}
              </Stack>
            </Stack>
            <Box marginTop="spacing-2-xl">
              <Stack gap={actionId === actionIds.GET_FREE_DOMAIN ? 'spacing-3-xs' : 'spacing-m'}>
                <ActionButton
                  fullWidth
                  isClipped={false}
                  size="extra-large"
                  format="solid"
                  fontSize="large"
                  appearance="accent"
                  label={t('Start with {term} {planName} –{space}FREE for 2 weeks{asterisks}', {
                    term: selectedTerm === ANNUAL ? t('Annual') : t('Monthly'),
                    planName: plan.name,
                    asterisks: actionId === actionIds.GET_FREE_DOMAIN ? '' : '*',
                    space: isSmBreakpoint || isXsBreakpoint ? ' ' : `<br />`,
                  })}
                  onClick={handleClick}
                />
                {actionId === actionIds.GET_FREE_DOMAIN ? (
                  <ActionButton
                    fullWidth
                    isClipped={false}
                    size="large"
                    format="outline"
                    fontSize="large"
                    appearance="default"
                    label={t('Maybe later')}
                    onClick={handleCancel}
                  />
                ) : (
                  <Paragraph appearance="medium-emphasis" align="center">
                    *
                    {actionId === actionIds.BUY_DOMAIN
                      ? `${t('Only pay for your domain today.')} `
                      : null}
                    {t('Cancel anytime.')} {!isMdBreakpoint && <br />}
                    <span className="automatic-plan-selector__cancel" onClick={handleCancel}>
                      {t('Explore other website plans.')}
                    </span>
                  </Paragraph>
                )}
              </Stack>
            </Box>
          </Box>
        </Col>
      </Row>
    </Container>
  );
};

AutomaticPlanSelector.propTypes = {
  actionId: PropTypes.string.isRequired,
  plan: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  isB2C: PropTypes.bool.isRequired,
  partnerId: PropTypes.string.isRequired,
  discountType: PropTypes.string,
  domainName: PropTypes.string,
  currency: PropTypes.string,
  successUrl: PropTypes.string,
  siteId: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default AutomaticPlanSelector;
